import { useMemo, useCallback } from 'react'

import { patchAccountLightweightSettings, PatchOperation } from 'redux/features/settings/settingsApiThunks'
import { AvailableSettings, EmailDomainExemptEntry, DomainEntry } from 'types/Settings'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isPending } from 'redux/toolkit/api'
import routesConfig from 'lib/routesConfig'

export interface State {
  isLoading: boolean
  tableData: DomainEntry[]
}

export interface EventHandlers {
  onAddItem: (item: DomainEntry) => void
  onRemoveItem: (id: string) => void
  onBulkEdit: () => void
}

export type UseWarningBannerExemptLogic = [State, EventHandlers]

export const useWarningBannerExemptLogic = (): UseWarningBannerExemptLogic => {
  const dispatch = useAppDispatch()
  const { accountSettings, updateAccountSettingPending } = useAppSelector(_stores => ({
    accountSettings: _stores.settings.accountSettings,
    updateAccountSettingPending: isPending(_stores.settings.updateAccountSettingsApiStatus)
  }))

  const warningBannerExempt = useMemo(() => {
    const exemptData = accountSettings.email_banners_sender_exempt
    if (!exemptData) return []
    if (Array.isArray(exemptData)) return exemptData as EmailDomainExemptEntry[]
    if (typeof exemptData === 'string') {
      try {
        return JSON.parse(exemptData) as EmailDomainExemptEntry[]
      } catch {
        return []
      }
    }
    return []
  }, [accountSettings])

  const tableData = useMemo(
    () =>
      warningBannerExempt.map(entry => ({
        domain: entry.name,
        comment: entry.comment,
        id: entry.id
      })) as DomainEntry[],
    [warningBannerExempt]
  )

  const onAddItem = useCallback(
    (item: DomainEntry) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.ADD,
          value: {
            name: item.domain,
            comment: item.comment,
            id: item.id
          },
          variable: AvailableSettings.EMAIL_BANNERS_SENDER_EXEMPT
        })
      )
    },
    [dispatch]
  )

  const onRemoveItem = useCallback(
    (id: string) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.REMOVE,
          value: { id },
          variable: AvailableSettings.EMAIL_BANNERS_SENDER_EXEMPT
        })
      )
    },
    [dispatch]
  )

  const onBulkEdit = useCallback(() => {
    routesConfig.INBOUND_SETTINGS_WARNING_BANNER_EXEMPT_BULK_EDIT.goto()
  }, [])

  return useMemo(
    () => [
      {
        isLoading: updateAccountSettingPending,
        tableData
      },
      {
        onAddItem,
        onRemoveItem,
        onBulkEdit
      }
    ],
    [updateAccountSettingPending, tableData, onAddItem, onRemoveItem, onBulkEdit]
  )
}
